"use client";

import { useState } from "react";
import Image from "next/image";
import { Images } from "@/assests";
import { motion } from "@/lib/motion";
import { Copy, Check, Smartphone, Terminal } from "@/components/icons";
import { cn } from "@/lib/utils";
import { PLAY_STORE_URL, LINUX_INSTALL_CMD } from "@/lib/constants";
import { GooglePlayBadge, EyebrowBadge } from "@/components/ui/";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function AndroidCard() {
  return (
    <motion.div
      variants={itemVariants}
      className="border-border bg-card group relative flex flex-col overflow-hidden rounded-2xl border"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--foreground) 5%, transparent), transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div className="border-border flex items-center gap-3 border-b px-6 py-5">
        <span className="light:text-blue-600 flex size-9 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-400">
          <Smartphone size={18} />
        </span>
        <div>
          <p className="text-foreground text-sm font-semibold">Android</p>
          <p className="text-muted-foreground text-xs">Google Play Store</p>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center gap-7 px-6 py-8">
        <p className="text-muted-foreground max-w-[22rem] text-center text-sm">
          Scan the QR code with your Android device or tap the button below to open Google Play.
        </p>

        <div className="border-border rounded-2xl border bg-white p-4 shadow-sm">
          <Image
            src={Images.proxidizeAndroidQr}
            alt="Scan to download Proxidize on Android"
            width={160}
            height={160}
            className="block"
          />
        </div>

        <p className="text-muted-foreground text-xs tracking-wide uppercase">or</p>

        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group transition-transform hover:-translate-y-0.5 active:translate-y-0"
          aria-label="Get it on Google Play"
        >
          <GooglePlayBadge />
        </a>
      </div>
    </motion.div>
  );
}

function LinuxCard() {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(LINUX_INSTALL_CMD).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <motion.div
      variants={itemVariants}
      className="border-border bg-card group relative flex flex-col overflow-hidden rounded-2xl border"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--foreground) 5%, transparent), transparent 70%)",
        }}
        aria-hidden="true"
      />
      <div className="border-border flex items-center gap-3 border-b px-6 py-5">
        <span className="flex size-9 items-center justify-center rounded-xl bg-[#2B72FF]/10 text-[#2B72FF]">
          <Terminal size={18} />
        </span>
        <div>
          <p className="text-foreground text-sm font-semibold">Linux</p>
          <p className="text-muted-foreground text-xs">One-line installer</p>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-start gap-7 px-6 py-8">
        <p className="text-muted-foreground text-sm">
          Run the command below in your Linux terminal to install the Proxidize Cloud client.
        </p>

        <div className="border-border w-full overflow-hidden rounded-xl border bg-[#111113]">
          <div className="flex items-center gap-1.5 border-b border-[#27272a] bg-[#161618] px-4 py-2.5">
            <span className="size-2.5 rounded-full bg-[#FF5F56]" />
            <span className="size-2.5 rounded-full bg-[#FFBD2E]" />
            <span className="size-2.5 rounded-full bg-[#27C93F]" />
          </div>

          <div className="flex items-center justify-between gap-3 px-4 py-4">
            <div className="flex min-w-0 items-start gap-2">
              <span className="mt-0.5 shrink-0 font-mono text-sm text-yellow-400">$</span>
              <code className="font-mono text-sm leading-relaxed break-all text-white/90">
                {LINUX_INSTALL_CMD}
              </code>
            </div>

            <button
              onClick={handleCopy}
              aria-label={copied ? "Copied!" : "Copy command"}
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-lg border transition-all duration-200",
                copied
                  ? "border-transparent bg-green-500/15 text-green-400"
                  : "border-[#27272a] bg-[#1C1C1E] text-[#a0a0a0] hover:bg-[#222224] hover:text-white"
              )}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>

        <motion.div
          initial={false}
          animate={copied ? { opacity: 1, y: 0 } : { opacity: 0, y: 4 }}
          transition={{ duration: 0.2 }}
          className="light:text-green-700 flex items-center gap-2 text-sm text-green-400"
          aria-live="polite"
        >
          <Check size={14} />
          <span>Command copied to clipboard!</span>
        </motion.div>

        <div className="mt-auto pt-2">
          <p className="text-muted-foreground text-xs leading-relaxed">
            Requires{" "}
            <code className="text-muted-foreground bg-muted/40 rounded px-1 py-0.5 font-mono text-xs">
              curl
            </code>{" "}
            and{" "}
            <code className="text-muted-foreground bg-muted/40 rounded px-1 py-0.5 font-mono text-xs">
              sudo
            </code>{" "}
            privileges. Compatible with Ubuntu 20.04+, Debian 10+, and most modern Linux
            distributions.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function DownloadSection() {
  return (
    <section
      className="relative overflow-hidden pt-32 pb-28 lg:pt-40 lg:pb-36"
      aria-label="Download Proxidize"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-cyan-400/[0.04] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center"
        >
          <motion.div variants={itemVariants}>
            <EyebrowBadge>Available now</EyebrowBadge>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="text-foreground mb-5 text-center text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
          >
            Download Proxidize
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-muted-foreground mb-14 max-w-xl text-center text-lg"
          >
            Get started on Android or Linux in seconds. Your mobile proxy hardware, wherever you
            are.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="grid w-full max-w-3xl grid-cols-1 gap-6 md:grid-cols-2"
          >
            <AndroidCard />
            <LinuxCard />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
